import * as React from 'react';

import {
  Input as AriaInput,
  TextArea as AriaTextArea,
  TextField as AriaTextField,
  TextFieldProps as AriaTextFieldProps,
  ValidationResult as AriaValidationResult,
  composeRenderProps,
} from 'react-aria-components';

import { cn, withColor } from '../../../lib/stylingUtils';
import { ThemeColor } from '../../../types/theme';
import { FieldError, FieldGroup, FormDescription, Label } from '../Field';
import { VirtualKeyboardWrapper } from '../keyboard/VirtualKeyboardWrapper';

export type TouchTextFieldProps = AriaTextFieldProps & {
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  label?: string;
  labelAdornment?: React.ReactNode;
  description?: string;
  innerLabel?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  preFocused?: boolean;
  variant?: 'flat' | 'outlined';
  color?: ThemeColor;
  errorMessage?: string | ((validation: AriaValidationResult) => string);
  touchModeEnabled?: boolean;
  disableVirtualKeyboard?: boolean;
  onFocusChange?: (focused: boolean) => void;
  fillLength?: number;
  fillChar?: string;
  placeholder?: string;
  rows?: number;
};

type TouchTextFieldInnerProps = Omit<
  TouchTextFieldProps,
  | 'className'
  | 'touchModeEnabled'
  | 'disableVirtualKeyboard'
  | 'label'
  | 'labelAdornment'
  | 'description'
  | 'textArea'
> & {
  shouldUseVirtualKeyboard: boolean;
};

const TouchTextFieldInner = ({
  inputClassName,
  innerLabel,
  prefix,
  suffix,
  preFocused,
  variant = 'flat',
  color,
  errorMessage,
  onFocusChange,
  fillLength,
  fillChar = '0',
  placeholder,
  rows = 1,
  shouldUseVirtualKeyboard,
  ...props
}: TouchTextFieldInnerProps) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const isTextArea = rows > 1;

  React.useEffect(() => {
    if (preFocused) {
      inputRef.current?.focus();
    }
  }, [preFocused]);

  React.useEffect(() => {
    onFocusChange?.(!!preFocused);
  }, [preFocused, onFocusChange]);

  const isInvalid = props.isInvalid || !!errorMessage;

  return (
    <AriaTextField
      className={composeRenderProps(inputClassName, (inputClassName) =>
        cn(
          '@TouchTextFieldInner',
          '@TouchTextFieldInput',
          'group flex h-full w-full flex-col',
          inputClassName,
        ),
      )}
      {...props}
      isInvalid={isInvalid}
    >
      <FieldGroup
        prefix={prefix}
        suffix={suffix}
        innerLabel={innerLabel}
        isDisabled={props.isDisabled}
        className={cn(
          '!h-full !min-h-full p-0 py-[5px] pr-[8px] pl-[6px]',
          '[font-size:inherit] text-neutral focus-within:bg-white [&_input]:[font-size:inherit] [&_input]:!text-[inherit]',
          isTextArea && 'h-auto',
          variant === 'outlined' && 'rounded-sm',
          variant === 'outlined' &&
            !props.isDisabled &&
            'border-custom-color/20 bg-white',
          variant === 'outlined' &&
            props.isDisabled &&
            'border-transparent bg-custom-color/5',
          variant !== 'outlined' &&
            'border-transparent bg-custom-color/5 rac-disabled:bg-white',
          color &&
            'bg-custom-color/10 text-custom-color focus-within:text-neutral',
        )}
      >
        {isTextArea ? (
          <AriaTextArea
            placeholder={placeholder}
            rows={rows}
            className="w-full bg-transparent px-1 py-1 selection:bg-current/20"
            style={{ fontSize: 'inherit' }}
            {...(shouldUseVirtualKeyboard
              ? { inputMode: 'none' as const }
              : {})}
          />
        ) : fillLength != null ? (
          <div className="relative w-full">
            <AriaInput
              ref={inputRef}
              onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                e.target.select()
              }
              className="relative z-1 w-full bg-transparent selection:bg-current/20"
              style={{ fontSize: 'inherit' }}
              maxLength={fillLength}
              {...(shouldUseVirtualKeyboard
                ? { inputMode: 'none' as const }
                : {})}
            />
            <div
              className="pointer-events-none absolute inset-0 flex items-center"
              style={{ fontSize: 'inherit' }}
            >
              <span className="opacity-0">
                {fillChar.repeat(props.value?.length ?? 0)}
              </span>
              <span className="opacity-10">
                {fillChar.repeat(
                  Math.max(0, fillLength - (props.value?.length ?? 0)),
                )}
              </span>
            </div>
          </div>
        ) : (
          <AriaInput
            ref={inputRef}
            onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
              e.target.select()
            }
            className="selection:bg-current/20"
            style={{ fontSize: 'inherit' }}
            placeholder={placeholder}
            {...(shouldUseVirtualKeyboard
              ? { inputMode: 'none' as const }
              : {})}
          />
        )}
      </FieldGroup>
      {errorMessage && <FieldError>{errorMessage}</FieldError>}
    </AriaTextField>
  );
};

export const TouchTextField = ({
  className,
  labelClassName,
  color,
  label,
  labelAdornment,
  description,
  rows,
  touchModeEnabled = false,
  disableVirtualKeyboard,
  ...props
}: TouchTextFieldProps) => {
  const shouldUseVirtualKeyboard = touchModeEnabled && !disableVirtualKeyboard;

  return (
    <div
      className={cn(
        '@TouchTextField group flex h-full flex-col gap-2 border-r-0 border-solid border-neutral/20 text-sm',
        className,
      )}
      style={withColor(color ?? 'neutral')}
    >
      {(label || labelAdornment) && (
        <div className="flex flex-row items-center gap-2">
          {label && (
            <Label className={labelClassName} aria-required={props.isRequired}>
              {label}
            </Label>
          )}
          {labelAdornment}
        </div>
      )}
      {shouldUseVirtualKeyboard ? (
        <VirtualKeyboardWrapper
          value={props.value ?? ''}
          onChange={props.onChange}
        >
          <TouchTextFieldInner
            shouldUseVirtualKeyboard={shouldUseVirtualKeyboard}
            color={color}
            rows={rows}
            {...props}
          />
        </VirtualKeyboardWrapper>
      ) : (
        <TouchTextFieldInner
          shouldUseVirtualKeyboard={shouldUseVirtualKeyboard}
          color={color}
          rows={rows}
          {...props}
        />
      )}
      {description && <FormDescription>{description}</FormDescription>}
    </div>
  );
};
